import { AlertTriangle, TrendingDown, BookOpen, Heart, ArrowRight, Activity } from "lucide-react";
import AnimatedSection from "@/utils/AnimatedSection";

const stats = [
  { value: "35.5%", label: "of Indian children under 5 are stunted due to chronic malnutrition", source: "NFHS-5, 2021" },
  { value: "67%", label: "of growth disorders go undiagnosed until it is too late for intervention", source: "Indian J. Pediatrics" },
  { value: "32%", label: "of school absenteeism in children is linked to preventable health issues", source: "WHO, 2023" },
  { value: "3x", label: "higher academic underperformance in children with untreated nutritional deficiencies", source: "Lancet Child Health" },
];

const problems = [
  {
    icon: AlertTriangle,
    title: "Undetected Malnutrition",
    color: "#FF6B6B",
    description: "India has the highest burden of child malnutrition globally. Micronutrient deficiencies in iron, zinc, vitamin D, and calcium silently impair cognitive development, weaken immunity, and stunt physical growth. By the time symptoms become visible, irreversible damage to brain development and bone density has already occurred.",
  },
  {
    icon: TrendingDown,
    title: "Hormonal & Growth Disorders",
    color: "#FFE66D",
    description: "Growth hormone deficiency, thyroid disorders, and early or delayed puberty affect 1 in 3,500 children. Without routine bone age assessment and endocrine screening, these conditions remain hidden during the critical growth window. A delay of even 1-2 years in diagnosis can permanently reduce adult height by 5-15 cm.",
  },
  {
    icon: BookOpen,
    title: "Academic & Developmental Decline",
    color: "#A855F7",
    description: "Research published in The Lancet shows that chronic nutritional deficiency directly impairs attention span, memory, and problem-solving ability. Malnourished children score 7-15% lower on standardized tests and are 3 times more likely to repeat a grade. Poor health leads to frequent absences, creating a cycle of declining performance.",
  },
  {
    icon: Heart,
    title: "Lifelong Health Consequences",
    color: "#4ECDC4",
    description: "Children with unmonitored growth issues face elevated risks of metabolic syndrome, type 2 diabetes, cardiovascular disease, and osteoporosis in adulthood. The WHO estimates that every $1 invested in childhood nutrition interventions yields $16 in long-term health and economic returns.",
  },
];

const WhyMonitoring = ({ theme }) => {
  return (
    <section id="why-monitoring-section" className="py-24 relative overflow-hidden bg-mesh" data-testid="why-monitoring-section">
      <div className="orb orb-coral w-[400px] h-[400px] -top-32 -left-20 opacity-40" />
      <div className="orb orb-purple w-[350px] h-[350px] bottom-0 right-0 opacity-30" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <span className="badge-glass inline-flex px-4 py-1.5 rounded-full text-xs font-semibold text-[#FF6B6B] mb-5 uppercase tracking-wider">
              The Silent Crisis
            </span>
            <h2 className="font-display text-4xl sm:text-5xl mb-4 text-white">
              Why Early Monitoring <span className="text-[#FF6B6B]">Saves Lives</span>
            </h2>
            <p className="text-lg max-w-3xl mx-auto text-white/50">
              Millions of children suffer from preventable conditions that go undetected until permanent damage is done.
              Here's what happens when growth and nutrition aren't monitored.
            </p>
          </div>
        </AnimatedSection>

        {/* Stats Row */}
        <AnimatedSection delay={0.1}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
            {stats.map((stat, i) => (
              <div key={i} className="glass-card p-5 text-center" data-testid={`monitoring-stat-${i}`}>
                <p className="text-3xl font-bold text-[#FF6B6B] font-display mb-1">{stat.value}</p>
                <p className="text-xs text-white/50 leading-relaxed mb-2">{stat.label}</p>
                <p className="text-[10px] text-white/25 italic">{stat.source}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Problem Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {problems.map((problem, i) => {
            const Icon = problem.icon;
            return (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="glass-card p-8 h-full" data-testid={`monitoring-problem-${i}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${problem.color}15` }}>
                      <Icon className="w-6 h-6" style={{ color: problem.color }} />
                    </div>
                    <div>
                      <h3 className="font-display text-xl text-white mb-3">{problem.title}</h3>
                      <p className="text-sm leading-relaxed text-white/50">{problem.description}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            );
          })}
        </div>

        {/* CTA: How tots360 helps */}
        <AnimatedSection delay={0.4}>
          <div className="glass-card p-8 md:p-10 text-center border border-[#4ECDC4]/20" data-testid="monitoring-cta">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center bg-[#4ECDC4]/10">
              <Activity className="w-8 h-8 text-[#4ECDC4]" />
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-white mb-4">
              tots360 Catches What Others Miss
            </h3>
            <p className="text-white/50 max-w-2xl mx-auto mb-6 leading-relaxed">
              Our AI-powered screening platform combines bone age analysis, nutritional profiling, and growth curve tracking
              to identify potential issues <strong className="text-white/70">months or years before symptoms appear</strong>.
              Early detection means timely intervention — giving every child the best chance at a healthy future.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { text: "Bone age within 0.5y accuracy", color: "#4ECDC4" },
                { text: "NIN-backed nutrition analysis", color: "#FF6B6B" },
                { text: "WHO growth curve tracking", color: "#FFE66D" },
                { text: "Personalized meal plans", color: "#A855F7" },
              ].map((item, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border" style={{ color: item.color, borderColor: `${item.color}30`, background: `${item.color}08` }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: item.color }} />
                  {item.text}
                </span>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default WhyMonitoring;
